import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { Button } from './ui/Button';
import { RecordPaymentDialog } from './collection/RecordPaymentDialog';
import { useAction } from '../auth/usePermission';
import { formatEUR, formatDate } from '../lib/format';

interface OnAccount {
  total: number;
  payments: Array<{
    payment_ledger_id: string;
    method: string;
    reference: string | null;
    created_at: string;
    value_date: string | null;
    amount: number;
    available: number;
  }>;
}

/**
 * Money received on account, not yet applied (migration 139). A garage on account may send a
 * transfer whenever he wants: it frees his available credit the day it lands, and the next monthly
 * recap deducts it from the amount due (the surplus carries to the month after). Recorded from
 * here even when no invoice is open — that is the case the invoice list cannot reach.
 */
export default function CustomerOnAccountMoney({
  customerId,
  onChanged,
}: {
  customerId: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const canRecord = useAction('INVOICE_PAY');
  const [data, setData] = useState<OnAccount | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    apiClient
      .get<OnAccount>(`/recap-invoices/customers/${customerId}/on-account`)
      .then((r) => setData(r.data))
      .catch(() => setData(null)); // not a role that sees money — no card
  }, [customerId]);
  useEffect(load, [load]);

  if (!data && !canRecord) return null;

  return (
    <section className="border rounded p-4 bg-card text-sm space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold">{t('relations.onAccount.title')}</h2>
        {canRecord && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t('relations.onAccount.record')}
          </Button>
        )}
      </div>
      <p>
        {t('relations.onAccount.received')} <strong>{formatEUR(data?.total ?? 0)}</strong>
        <span className="ml-2 text-xs text-muted-foreground">
          {t('relations.onAccount.receivedHint')}
        </span>
      </p>
      {data && data.payments.length > 0 && (
        <ul className="text-xs text-muted-foreground">
          {data.payments.map((p) => (
            <li key={p.payment_ledger_id}>
              {formatDate(p.value_date ?? p.created_at)} —{' '}
              {t(`domain.paymentMethod.${p.method}`, { defaultValue: p.method })}
              {p.reference ? ` (${p.reference})` : ''} — {formatEUR(p.available)}
              {p.available < p.amount
                ? t('relations.onAccount.ofAmount', { amount: formatEUR(p.amount) })
                : ''}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <RecordPaymentDialog
          customerId={customerId}
          onClose={() => setOpen(false)}
          onDone={() => {
            load();
            onChanged();
          }}
        />
      )}
    </section>
  );
}
